import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {HttpError, HttpErrorBackend, HttpErrorProject} from '@specials/project-user';
import {
  CaptureEventParams,
  CaptureExceptionParams,
  CaptureExceptionPayload,
  SentryBrowserAbstract,
} from '@specials/sentry-browser';

import {ErrorBreadcrumbs} from '@/services/ErrorsService/ErrorsService';
import {FrontendError} from '@/services/ErrorsService/FrontendError';
import {UserStore} from '@/stores/UserStore/UserStore';
import {Config} from '@/types';
import {UIErrorComponent} from '@/ui/common/AppError/components/constants';

export class LoggerService {
  private userId: string | undefined;
  private cookies: string | undefined;

  private readonly errorNames = {
    ErrorBoundaryError: 'ErrorBoundaryError',
    HttpErrorBackend: 'HttpErrorBackend',
    HttpErrorProject: 'HttpErrorProject',
    HttpErrorUnknown: 'HttpErrorUnknown',
    UnmatchedError: 'UnmatchedError',
    NativeShareError: 'NativeShareError',
    CopyToClipboardError: 'CopyToClipboardError',
    InvalidOnboardingIndexError: 'InvalidOnboardingIndexError',
    HaveNotSubtaskForClickLink: 'HaveNotSubtaskForClickLink',
    UIErrorFullscreenComponent: 'UIErrorFullscreenComponent',
    UIErrorCardComponent: 'UIErrorCardComponent',
    UIErrorForCTAComponent: 'UIErrorForCTAComponent',
  };

  constructor(
    private readonly config: Config,
    private readonly sentryBrowser: SentryBrowserAbstract,
    private readonly userStore: UserStore,
  ) {}

  public setUserId(userId: string | undefined): void {
    this.userId = userId;
  }

  public setCookies(cookies: string | undefined): void {
    this.cookies = cookies;
  }

  public logErrorBoundaryError(error: FrontendError): void {
    this.error({
      message: this.errorNames.ErrorBoundaryError,
      trackingId: error.requestId,
      payload: {
        errorCode: error.errorCode,
        message: error.message,
        timestamp: error.timestamp,
      },
      error: error.error,
    });
  }

  public logHttpError(error: HttpError): void {
    if (HttpErrorProject.isHttpErrorProject(error)) {
      this.error({
        message: this.errorNames.HttpErrorProject,
        trackingId: error.requestId,
        payload: {
          errorCode: error.errorCode,
          message: error.message,
          timestamp: error.timestamp,
        },
        error,
      });

      return;
    }

    if (HttpErrorBackend.isHttpErrorBackend(error)) {
      this.error({
        message: this.errorNames.HttpErrorBackend,
        payload: {
          httpCode: error.response.httpCode,
          message: error.message,
          name: error.name,
        },
        error,
      });

      return;
    }

    this.error({
      message: this.errorNames.HttpErrorUnknown,
      payload: {
        message: error.message,
        name: error.name,
      },
      error,
    });
  }

  // логируем отображение UI компонента ошибки
  public logDisplayErrorUIComponent(
    error: FrontendError | HttpError | unknown,
    breadcrumbs: ErrorBreadcrumbs,
    component: UIErrorComponent,
  ): void {
    let message = '';

    switch (component) {
      case UIErrorComponent.Card: {
        message = this.errorNames.UIErrorCardComponent;
        break;
      }

      case UIErrorComponent.Fullscreen: {
        message = this.errorNames.UIErrorFullscreenComponent;
        break;
      }

      case UIErrorComponent.ForCTA: {
        message = this.errorNames.UIErrorForCTAComponent;
        break;
      }

      default: {
        message = this.errorNames.UIErrorCardComponent;
        break;
      }
    }

    if (HttpErrorProject.isHttpErrorProject(error)) {
      this.warn({
        message,
        trackingId: error.requestId,
        payload: {
          message: error.errorMessage,
          timestamp: error.timestamp,
          errorName: this.errorNames.HttpErrorProject,
          ...breadcrumbs,
        },
      });

      return;
    }

    if (HttpErrorBackend.isHttpErrorBackend(error)) {
      this.warn({
        message,
        payload: {
          httpCode: error.response.httpCode,
          message: error.message,
          name: error.name,
          errorName: this.errorNames.HttpErrorBackend,
          ...breadcrumbs,
        },
      });

      return;
    }

    if (FrontendError.isFrontendError(error)) {
      this.warn({
        message,
        trackingId: error.requestId,
        payload: {
          message: error.message,
          timestamp: error.timestamp,
          errorName: error.name,
          ...breadcrumbs,
        },
      });

      return;
    }

    this.warn({
      message,
      payload: {
        errorName: this.errorNames.HttpErrorUnknown,
        ...breadcrumbs,
      },
    });
  }

  public logUnMatchedError(breadcrumbs: ErrorBreadcrumbs): void {
    this.error({
      message: this.errorNames.UnmatchedError,
      trackingId: breadcrumbs.requestId,
      payload: {
        ...breadcrumbs,
      },
    });
  }

  public logNativeShareError(error: Error): void {
    this.error({
      message: this.errorNames.NativeShareError,
      error,
    });
  }

  public logCopyToClipboardError(error: Error): void {
    this.error({
      message: this.errorNames.CopyToClipboardError,
      error,
    });
  }

  public logHaveNotSubtaskForClickLinkError(taskId: string): void {
    this.error({
      message: this.errorNames.HaveNotSubtaskForClickLink,
      payload: {
        taskId,
      },
    });
  }

  public logHaveInvalidOnboardingIndexError(index: number): void {
    this.error({
      message: this.errorNames.InvalidOnboardingIndexError,
      payload: {
        index,
      },
    });
  }

  public handleCustomError(message: string, error: Error, payload?: CaptureExceptionPayload): void {
    if (!this.config.isProd) {
      console.error(message);
    }

    this.sentryBrowser.captureException({
      message,
      error,
      payload,
    });
  }

  public log(message: unknown): void {
    if (!this.config.isProd) {
      console.log(message);
    }
  }

  public logError(...args: unknown[]): void {
    if (!this.config.isProd) {
      console.error(...args);
    }
  }

  private warning(message: string): void {
    if (!this.config.isProd) {
      console.warn(message);
    }
  }

  private error(params: CaptureExceptionParams): void {
    if (!this.config.isProd) {
      console.error(params.message, params.error);
    }

    const sentParams: CaptureExceptionParams = {
      message: params.message,
      error: params.error as Error,
      trackingId: params?.trackingId,
      payload: {
        ...params.payload,
        userId: this.userId,
        cookies: this.cookies,
      },
    };

    this.sentryBrowser.captureException(sentParams);
  }

  private warn(params: CaptureEventParams): void {
    if (!this.config.isProd) {
      console.error(params.message, params.payload);
    }

    const sentParams: CaptureEventParams = {
      message: params.message,
      trackingId: params?.trackingId,
      payload: {
        ...params.payload,
        userId: this.userId,
        cookies: this.cookies,
      },
    };

    // отправляем события через captureEvent,
    // т.к. в captureException встроен троттлинг на отправку сообщений
    this.sentryBrowser.captureEvent(sentParams);
  }
}

injected(LoggerService, DI_TOKENS.config, DI_TOKENS.sentryBrowser, DI_TOKENS.userStore);
